import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createBulkPurchase, searchProducts } from '../../services/purchaseProgramApi';

const STEPS = ['Program', 'Users', 'Products', 'Review & Confirm'];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: 28, overflowX: 'auto' }}>
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, flex: 1 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14,
                background: done ? '#057a55' : active ? '#1a56db' : '#e5e7eb',
                color: done || active ? '#fff' : '#6b7280',
                transition: 'all .2s',
              }}>
                {done ? <i className="bi bi-check-lg" aria-hidden="true" style={{ fontSize: 14 }} /> : i + 1}
              </div>
              <span style={{ fontSize: 12, marginTop: 4, color: active ? '#1a56db' : done ? '#057a55' : '#9ca3af', fontWeight: active ? 700 : 400, textAlign: 'center' }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, alignSelf: 'center', background: done ? '#057a55' : '#e5e7eb', marginBottom: 20, transition: 'background .2s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Admin product card ───────────────────────────────────────────────────────
function AdminProductCard({ product, cartVal, onChange }) {
  const [imgErr, setImgErr] = useState(false);
  const inCart = Number(cartVal?.qty) > 0;
  return (
    <div style={{
      background: '#fff', border: `2px solid ${inCart ? '#1a56db' : '#e5e7eb'}`,
      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      transition: 'border-color .15s, box-shadow .15s',
      boxShadow: inCart ? '0 0 0 3px rgba(26,86,219,.12)' : '0 1px 3px rgba(0,0,0,.06)',
    }}>
      <div style={{ width: '100%', aspectRatio: '1/1', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        {inCart && (
          <span style={{ position: 'absolute', top: 8, right: 8, background: '#1a56db', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 9999 }}>
            {cartVal.qty} ordered
          </span>
        )}
        {product.image_url && !imgErr
          ? <img src={product.image_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} onError={() => setImgErr(true)} loading="lazy" />
          : <i className="bi bi-box-seam" style={{ fontSize: 40, color: '#d1d5db' }} aria-hidden="true" />
        }
      </div>
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {product.category && (
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#6366f1', fontWeight: 600 }}>{product.category}</span>
        )}
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {product.name || product.product_code}
        </div>
        {product.manufacturer && <div style={{ fontSize: 12, color: '#6b7280' }}>{product.manufacturer}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
          <code style={{ fontSize: 10, color: '#9ca3af' }}>#{product.part_number || product.product_code}</code>
          {product.price > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>${Number(product.price).toFixed(2)}</span>}
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 8, alignItems: 'center' }}>
          <label htmlFor={`oblf-qty-${product.product_code}`} className="visually-hidden">Qty</label>
          <input
            id={`oblf-qty-${product.product_code}`}
            type="number" min="0" placeholder="Qty"
            className="form-control form-control-sm"
            style={{ width: 60, fontSize: 13 }}
            value={cartVal?.qty || ''}
            onChange={e => onChange({ ...cartVal, qty: e.target.value })}
          />
          <label htmlFor={`oblf-price-${product.product_code}`} className="visually-hidden">Price</label>
          <div className="input-group input-group-sm" style={{ flex: 1 }}>
            <span className="input-group-text" style={{ fontSize: 12 }}>$</span>
            <input
              id={`oblf-price-${product.product_code}`}
              type="number" min="0" step="0.01" placeholder="Price"
              className="form-control"
              style={{ fontSize: 13 }}
              value={cartVal?.price || product.price || ''}
              onChange={e => onChange({ ...cartVal, price: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PurchaseOnBehalfTab({ programs, userGroups, adminUserId = '' }) {
  const [step, setStep] = useState(0);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [userIdsText, setUserIdsText] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [products, setProducts] = useState([]);
  const [productTotal, setProductTotal] = useState(0);
  const [productFacets, setProductFacets] = useState({ categories: [], manufacturers: [] });
  const [productSearch, setProductSearch] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productPage, setProductPage] = useState(1);
  const PAGE_SIZE = 48;
  const [productsLoading, setProductsLoading] = useState(false);
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const searchTimer = useRef(null);

  const selectedProgram = programs.find(p => p.id === selectedProgramId);
  const selectedGroup = userGroups.find(g => g.id === Number(selectedGroupId));

  const parsedUserIds = useMemo(() => {
    const fromText = userIdsText.split(/[\n,]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const fromGroup = selectedGroupId && selectedGroup
      ? (selectedGroup.members || []).map(m => typeof m === 'object' ? m.user_id : m)
      : [];
    const all = [...new Set([...fromGroup, ...fromText])];
    return all;
  }, [userIdsText, selectedGroupId, selectedGroup]);

  const cartItems = useMemo(() =>
    Object.entries(cart)
      .map(([code, v]) => ({ product_code: code, quantity: Number(v.qty || 0), unit_price: Number(v.price || 0) }))
      .filter(i => i.quantity > 0),
    [cart]);

  const doLoadProducts = useCallback((q, cat, pg) => {
    if (!selectedProgramId) return;
    setProductsLoading(true);
    searchProducts({ q, category: cat, programId: selectedProgramId, page: pg, pageSize: PAGE_SIZE })
      .then(res => {
        setProducts(res?.products || []);
        setProductTotal(res?.total || 0);
        setProductFacets(res?.facets || { categories: [], manufacturers: [] });
      })
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, [selectedProgramId]);

  useEffect(() => {
    if (step === 2 && selectedProgramId) doLoadProducts(productSearch, productCategory, productPage);
  }, [step, selectedProgramId]);

  const handleSubmit = async () => {
    if (!selectedProgramId || parsedUserIds.length === 0 || cartItems.length === 0) return;
    setSubmitting(true);
    try {
      const res = await createBulkPurchase({
        purchased_by: adminUserId || 'admin',
        program_id: selectedProgramId,
        user_ids: parsedUserIds,
        items: cartItems,
        notes: notes || undefined,
      });
      setResult(res);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(0); setSelectedProgramId(null); setUserIdsText(''); setSelectedGroupId('');
    setProducts([]); setCart({}); setNotes(''); setResult(null);
  };

  if (result) {
    const { bulk_purchase_id, total_users, success_count, failed_count, results: userResults, error } = result;
    return (
      <div>
        <div className="card" style={{ borderRadius: 12, border: '1px solid #e5e7eb', padding: '24px 28px', maxWidth: 640, margin: '0 auto' }}>
          {error ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <i className="bi bi-x-circle-fill" aria-hidden="true" style={{ fontSize: 32, color: '#c81e1e' }} />
              </div>
              <h3 style={{ fontWeight: 700, color: '#c81e1e' }}>Error</h3>
              <p style={{ color: '#6b7280' }}>{error}</p>
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: failed_count === 0 ? '#d1fae5' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                  <i className={`bi ${failed_count === 0 ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} aria-hidden="true" style={{ fontSize: 30, color: failed_count === 0 ? '#059669' : '#d97706' }} />
                </div>
                <h3 style={{ fontWeight: 700, color: '#111827' }}>Bulk Purchase Complete</h3>
                <p style={{ color: '#6b7280' }}>Bulk #{bulk_purchase_id} — {success_count} succeeded, {failed_count} failed out of {total_users} users</p>
              </div>
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {(userResults || []).map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13,
                  }}>
                    <code style={{ fontSize: 12 }}>{r.user_id}</code>
                    {r.saved
                      ? <span style={{ color: '#057a55', fontWeight: 600 }}><i className="bi bi-check-lg me-1" aria-hidden="true" />${r.purchase?.total_amount?.toFixed(2)}</span>
                      : <span style={{ color: '#c81e1e', fontSize: 12 }}><i className="bi bi-x-lg me-1" aria-hidden="true" />{r.validation?.reasons?.[0] || r.error || 'Failed'}</span>
                    }
                  </div>
                ))}
              </div>
            </>
          )}
          <button className="btn btn-outline-primary mt-4 w-100" onClick={handleReset} style={{ marginTop: 20 }}>
            <i className="bi bi-arrow-counterclockwise me-1" aria-hidden="true" />Start New Bulk Purchase
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StepIndicator current={step} />

      {/* Step 0: Select program */}
      {step === 0 && (
        <div className="row g-3">
          <div className="col-12">
            <p style={{ color: '#6b7280', marginBottom: 16 }}>Select the program under which you'll purchase on behalf of users.</p>
          </div>
          {programs.map(prog => (
            <div key={prog.id} className="col-xl-4 col-md-6">
              <button
                style={{
                  width: '100%', textAlign: 'left', padding: '16px 18px', borderRadius: 10,
                  border: `2px solid ${selectedProgramId === prog.id ? '#1a56db' : '#e5e7eb'}`,
                  background: selectedProgramId === prog.id ? '#eff6ff' : '#fff',
                  cursor: 'pointer', transition: 'all .15s',
                }}
                onClick={() => setSelectedProgramId(prog.id)}
                aria-pressed={selectedProgramId === prog.id}
              >
                <div style={{ fontWeight: 700, marginBottom: 4, color: '#111827' }}>{prog.name}</div>
                {prog.amount_limit != null && (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    ${prog.amount_used?.toFixed(0)} / ${prog.amount_limit?.toLocaleString()} used
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 5, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
                    {prog.amount_limit && (
                      <div style={{ height: '100%', width: `${Math.min(100, Math.round((prog.amount_used / prog.amount_limit) * 100))}%`, background: '#1a56db', borderRadius: 99 }} />
                    )}
                  </div>
                </div>
              </button>
            </div>
          ))}
          <div className="col-12 d-flex justify-content-end mt-2">
            <button className="btn btn-primary" disabled={!selectedProgramId} onClick={() => setStep(1)} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
              Next: Select Users<i className="bi bi-arrow-right ms-1" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Select users */}
      {step === 1 && (
        <div>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Choose which users to purchase for. Use a group, paste IDs, or both.</p>
          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label fw-semibold" htmlFor="behalf-group">Add from User Group</label>
              <select id="behalf-group" className="form-select" value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)}>
                <option value="">— select group —</option>
                {userGroups.map(g => <option key={g.id} value={g.id}>{g.group_name} ({g.member_count || 0})</option>)}
              </select>
            </div>
            <div className="col-md-7">
              <label className="form-label fw-semibold" htmlFor="behalf-ids">Additional User IDs (comma or newline separated)</label>
              <textarea id="behalf-ids" className="form-control font-monospace" rows={5} style={{ fontSize: 13 }} value={userIdsText} onChange={e => setUserIdsText(e.target.value)} placeholder="user1, user2, user3" />
            </div>
          </div>
          <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, display: 'inline-block', fontSize: 13 }}>
            <strong>{parsedUserIds.length}</strong> user{parsedUserIds.length !== 1 ? 's' : ''} selected
            {parsedUserIds.length > 0 && `: ${parsedUserIds.slice(0, 5).join(', ')}${parsedUserIds.length > 5 ? ` …+${parsedUserIds.length - 5} more` : ''}`}
          </div>
          <div className="d-flex gap-2 justify-content-between mt-4">
            <button className="btn btn-outline-secondary" onClick={() => setStep(0)}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
            <button className="btn btn-primary" disabled={parsedUserIds.length === 0} onClick={() => setStep(2)} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
              Next: Select Products<i className="bi bi-arrow-right ms-1" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select products */}
      {step === 2 && (
        <div>
          {/* Search + category filter bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 240px' }}>
              <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }} aria-hidden="true" />
              <input
                className="form-control"
                style={{ paddingLeft: 32, paddingRight: productSearch ? 30 : undefined }}
                placeholder="Search by name, part number, brand…"
                value={productSearch}
                onChange={e => {
                  const v = e.target.value;
                  setProductSearch(v); setProductPage(1);
                  clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => doLoadProducts(v, productCategory, 1), 350);
                }}
                aria-label="Search products"
              />
              {productSearch && (
                <button style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}
                  onClick={() => { setProductSearch(''); setProductPage(1); doLoadProducts('', productCategory, 1); }} aria-label="Clear search">
                  <i className="bi bi-x" />
                </button>
              )}
            </div>
            {productFacets.categories?.length > 0 && (
              <select
                className="form-select" style={{ flex: '0 0 auto', width: 'auto', maxWidth: 200, fontSize: 13 }}
                value={productCategory}
                onChange={e => { setProductCategory(e.target.value); setProductPage(1); doLoadProducts(productSearch, e.target.value, 1); }}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {productFacets.categories.map(c => <option key={c.value} value={c.value}>{c.value} ({c.count})</option>)}
              </select>
            )}
            {productsLoading && <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />}
            {!productsLoading && <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{productTotal.toLocaleString()} products</span>}
          </div>

          {/* Cart summary bar */}
          {cartItems.length > 0 && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <i className="bi bi-cart3 text-primary" aria-hidden="true" />
              <strong>{cartItems.length}</strong> product{cartItems.length !== 1 ? 's' : ''} selected
              <span style={{ color: '#6b7280' }}>·</span>
              <span style={{ color: '#6b7280' }}>{cartItems.map(i => `${i.product_code}×${i.quantity}`).slice(0, 4).join(', ')}{cartItems.length > 4 ? ` +${cartItems.length - 4} more` : ''}</span>
            </div>
          )}

          {/* Product grid */}
          <div className="row g-3">
            {productsLoading && Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                <div style={{ background: '#f3f4f6', borderRadius: 12, aspectRatio: '1/1', animation: 'pulse 1.5s infinite' }} />
              </div>
            ))}
            {!productsLoading && products.map(prod => (
              <div key={prod.product_code} className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                <AdminProductCard
                  product={prod}
                  cartVal={cart[prod.product_code]}
                  onChange={v => setCart(c => {
                    if (!v?.qty && !v?.price) { const { [prod.product_code]: _, ...rest } = c; return rest; }
                    return { ...c, [prod.product_code]: v };
                  })}
                />
              </div>
            ))}
            {!productsLoading && products.length === 0 && (
              <div className="col-12 text-center py-5" style={{ color: '#9ca3af' }}>
                <i className="bi bi-search" style={{ fontSize: 32, display: 'block', marginBottom: 10 }} aria-hidden="true" />
                No products found{productSearch ? ` for "${productSearch}"` : ''}.
              </div>
            )}
          </div>

          {/* Pagination */}
          {productTotal > PAGE_SIZE && (
            <div className="d-flex justify-content-center gap-1 mt-4 flex-wrap">
              <button className="btn btn-sm btn-outline-secondary" disabled={productPage <= 1} onClick={() => { const p = productPage - 1; setProductPage(p); doLoadProducts(productSearch, productCategory, p); }}>
                <i className="bi bi-chevron-left" />
              </button>
              {Array.from({ length: Math.min(7, Math.ceil(productTotal / PAGE_SIZE)) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`btn btn-sm ${p === productPage ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setProductPage(p); doLoadProducts(productSearch, productCategory, p); }}
                  style={p === productPage ? { background: '#1a56db', borderColor: '#1a56db' } : undefined}
                >{p}</button>
              ))}
              <button className="btn btn-sm btn-outline-secondary" disabled={productPage >= Math.ceil(productTotal / PAGE_SIZE)} onClick={() => { const p = productPage + 1; setProductPage(p); doLoadProducts(productSearch, productCategory, p); }}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}

          <div className="d-flex gap-2 justify-content-between mt-4">
            <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
            <button className="btn btn-primary" disabled={cartItems.length === 0} onClick={() => setStep(3)} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
              Next: Review<i className="bi bi-arrow-right ms-1" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review & Confirm */}
      {step === 3 && (
        <div>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#374151' }}>Summary</h4>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><td style={{ color: '#6b7280', paddingBottom: 4 }}>Program</td><td style={{ fontWeight: 600 }}>{selectedProgram?.name}</td></tr>
                    <tr><td style={{ color: '#6b7280', paddingBottom: 4 }}>Users</td><td style={{ fontWeight: 600 }}>{parsedUserIds.length}</td></tr>
                    <tr><td style={{ color: '#6b7280', paddingBottom: 4 }}>Products</td><td style={{ fontWeight: 600 }}>{cartItems.length}</td></tr>
                    <tr><td style={{ color: '#6b7280' }}>Est. total / user</td><td style={{ fontWeight: 600 }}>${cartItems.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#374151' }}>Products</h4>
                {cartItems.map(i => (
                  <div key={i.product_code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingBottom: 4 }}>
                    <span><code style={{ fontSize: 11 }}>{i.product_code}</code> × {i.quantity}</span>
                    <span style={{ color: '#374151', fontWeight: 600 }}>${(i.quantity * i.unit_price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-3" style={{ maxWidth: 500 }}>
            <label className="form-label fw-semibold" htmlFor="behalf-notes">Notes (optional)</label>
            <textarea id="behalf-notes" className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Q1 2024 PPE distribution" />
          </div>

          <div className="d-flex gap-2 justify-content-between mt-2">
            <button className="btn btn-outline-secondary" onClick={() => setStep(2)} disabled={submitting}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
            <button
              className="btn btn-success"
              style={{ background: '#057a55', borderColor: '#057a55', minWidth: 180 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Processing…</>
                : <><i className="bi bi-bag-check me-1" aria-hidden="true" />Confirm Purchase for {parsedUserIds.length} Users</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
